import { createContext, ReactNode, useEffect, useState } from "react";
import axios from "axios";


// interface
import {
  SectionData,
  SectionList,
  EvaluationScope,
} from "../interfaces/questionnaire";
import { ErrorParams } from "../interfaces/errorParams";

import { ActionList, ActionData } from "../interfaces/actionsPlanned";

type OptionCountProp = {
  count: number;
  percent: number;
};

interface SelectedOptionCount {
  totalSelected: number;
  yes: OptionCountProp;
  no: OptionCountProp;
  na: OptionCountProp;
  tbd: OptionCountProp;
}

interface QuestionnaireFormData {
  evalScope: any;
  fetchEvaluationInfo: any;
  sections: SectionData[];
  questionLoading: boolean;
  questionCount: number;
  handleResponseSelect: any;
  sectionList: SectionList[];
  selectedSection: any;
  scrollToSection: any;
  fetchSectionsList: any;
  selectedOptionCount: SelectedOptionCount;
  handleSaveEvaluationResponse: any;
  handleResponseTextInputChange: any;
  actionsPlanned: ActionList;
  actionCount: number;
  errorParams:ErrorParams[];
  removeCollaborator:any;
}

export const QuestionnaireContext = createContext<QuestionnaireFormData>({
  evalScope: {},
  fetchEvaluationInfo: () => {},
  sections: [],
  questionLoading: true,
  questionCount: 0,
  handleResponseSelect: () => {},
  sectionList: [],
  selectedSection: {},
  scrollToSection: () => {},
  fetchSectionsList: () => {},
  selectedOptionCount: {
    totalSelected: 0,
    yes: {
      count: 0,
      percent: 0,
    },
    no: {
      count: 0,
      percent: 0,
    },
    na: {
      count: 0,
      percent: 0,
    },
    tbd: {
      count: 0,
      percent: 0,
    },
  },
  handleSaveEvaluationResponse: () => {},
  handleResponseTextInputChange: () => {},
  actionsPlanned: {
    evaluation_id: "",
    sections: [],
  },
  actionCount: 0,
  errorParams:[],
  removeCollaborator:() => {}
});

type QuestionnaireContextProviderProps = {
  children: ReactNode;
};

const API_URL = "/api/v1";

export const QuestionnaireContextProvider = ({
  children,
}: QuestionnaireContextProviderProps) => {
  const [evalScope, setEvalScope] = useState<EvaluationScope>({
    _id: "",
    user_id: "",
    name: "",
    description: "",
    maturity: "",
    sub_maturity: [],
    easa_system_level: "",
    evaluation_type: "",
    collaborators: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  });
  const [sectionList, setSectionList] = useState<SectionList[]>([]);
  const [selectedSection, setSelectedSection] = useState<any>({
    value: "navigate",
    label: "Navigate to...",
    color: "none",
  });
  const [sections, setSections] = useState<SectionData[]>([]);
  const [questionLoading, setQuestionLoading] = useState<boolean>(true);
  const [questionCount, setQuestionCount] = useState<number>(0);
  const [selectedOptionCount, setSelectedOptionCount] = useState({
    totalSelected: 0,
    yes: {
      count: 0,
      percent: 0,
    },
    no: {
      count: 0,
      percent: 0,
    },
    na: {
      count: 0,
      percent: 0,
    },
    tbd: {
      count: 0,
      percent: 0,
    },
  });
  
  const [actionsPlanned, setActionsPlanned] = useState<ActionList>({
    evaluation_id: "",
    sections: [],
  });
  const [actionCount, setActionCount] = useState<number>(0);
  const [errorParams, setErrorParams] = useState<ErrorParams[]>([]);

  const fetchSectionsList = async (id: string) => {
    try {
      const res = await axios.get(`${API_URL}/evaluation/section/list/${id}`);
      if (res.status === 200 && res.data.success === true) {
        if (res.data.sections.length > 0) {
          setSelectedSection({
            value: "navigate",
            label: "Navigate to...",
            color: "none",
          });
          setSectionList(res.data.sections);
        }
      }
    } catch (error) {
      console.log("Error fetching section list", error);
    }
  };

  const fetchEvaluationInfo = async (id: string) => {
    try {
      setQuestionLoading(true);
      setErrorParams([]);
      const res = await axios.get(`${API_URL}/evaluation/info/${id}`);
      if (res.status === 200 && res.data.success === true) {
        fetchSectionsList(id);
        setEvalScope(res.data.scopeInfo);
        setSections(res.data.sections);
        setQuestionCount(res.data.questionCount);
        console.log(res.data.sections)
        return true;
      }
    } catch (error:any) {
      // console.log("Error fetching evaluation info:", error);
      if(error.status === 400 || error.status === 401){
        return false;
      }
    } finally {
      setQuestionLoading(false);
    }
  };

  const scrollToSection = (value: SectionList) => {
    setSelectedSection(value);
  };

  const handleResponseSelect = (
    section_id: string,
    subsection_id: any,
    question_id: string,
    response: string
  ) => {
    setSections(
      sections.map((sec) => {
        if (sec._id.toString() === section_id.trim()) {
          if (sec.questions.length > 0) {
            let questions = sec.questions.map((quest) => {
              if (quest.question_id.toString().trim() === question_id.trim()) {
                if(response === "yes" || response === "na"){
                  return {
                    ...quest,
                    actions_planned :"",
                    response: response
                  };
                }else{
                  return {
                    ...quest,
                    actions_planned :"",
                    response: response
                  };
                }
                
              } else {
                return quest;
              }
            });
            return {
              ...sec,
              questions: questions,
            };
          } else {
            return {
              ...sec,
              sub_sections: sec.sub_sections.map((subsec) => {
                if (
                  subsec.sub_section_id.toString().trim() ===
                  subsection_id.trim()
                ) {
                  let questions = subsec.questions.map((quest) => {
                    if (
                      quest.question_id.toString().trim() === question_id.trim()
                    ) {
                      if(response === "yes" || response === "na"){
                        return {
                          ...quest,
                          actions_planned :"",
                          response: response
                        };
                      }else{
                        return {
                          ...quest,
                          actions_planned :"",
                          response: response
                        };
                      }
                    } else {
                      return quest;
                    }
                  });
                  return {
                    ...subsec,
                    questions: questions,
                  };
                } else {
                  return subsec;
                }
              }),
            };
          }
        } else {
          return sec;
        }
      })
    );
  };

  // TODO: Fix this function: replace the index with data component id
  const handleResponseTextInputChange = (
    section_id: string,
    subsection_id: any,
    question_id: string,
    inputType: string,
    value: string
  ) => {
    setSections(
      sections.map((sec) => {
        if (sec._id.toString().trim() === section_id.trim()) {
          if (sec.questions.length > 0) {
            let questions = sec.questions.map((quest) => {
              if (quest.question_id.toString().trim() === question_id.trim()) {
                if (inputType === "justification") {
                  return {
                    ...quest,
                    justification: value,
                  };
                } else if (inputType === "actions") {
                  return {
                    ...quest,
                    actions_planned: value,
                  };
                } else {
                  return {
                    ...quest,
                    note: value,
                  };
                }
              } else {
                return quest;
              }
            });

            return {
              ...sec,
              questions: questions,
            };
          } else {
            return {
              ...sec,
              sub_sections: sec.sub_sections.map((subsec) => {
                if (
                  subsec.sub_section_id.toString().trim() ===
                  subsection_id.trim()
                ) {
                  let questions = subsec.questions.map((quest) => {
                    if (
                      quest.question_id.toString().trim() === question_id.trim()
                    ) {
                      if (inputType === "justification") {
                        return {
                          ...quest,
                          justification: value,
                        };
                      } else if (inputType === "actions") {
                        return {
                          ...quest,
                          actions_planned: value,
                        };
                      } else {
                        return {
                          ...quest,
                          note: value,
                        };
                      }
                    } else {
                      return quest;
                    }
                  });
                  return {
                    ...subsec,
                    questions: questions,
                  };
                } else {
                  return subsec;
                }
              }),
            };
          }
        } else {
          return sec;
        }
      })
    );
  };

  const handleOptionCountToPercent = () => {
    let totalSelected = 0;
    let yesCount = 0;
    let noCount = 0;
    let naCount = 0;
    let tbdCount = 0;

    let yesPercent = 0;
    let noPercent = 0;
    let naPercent = 0;
    let tbdPercent = 0;

    sections.forEach((section) => {
      if (section.questions.length > 0) {
        section.questions.forEach((question) => {
          if (question.response === "yes") {
            yesCount += 1;
          } else if (question.response === "no") {
            noCount += 1;
          } else if (question.response === "na") {
            naCount += 1;
          } else if (question.response === "tbd") {
            tbdCount += 1;
          }
        });
      } else {
        section.sub_sections.forEach((subsec) => {
          subsec.questions.forEach((question) => {
            if (question.response === "yes") {
              yesCount += 1;
            } else if (question.response === "no") {
              noCount += 1;
            } else if (question.response === "na") {
              naCount += 1;
            } else if (question.response === "tbd") {
              tbdCount += 1;
            }
          });
        });
      }
    });
    totalSelected = yesCount + noCount + naCount + tbdCount;
    if (yesCount === 0 && noCount === 0 && naCount === 0 && tbdCount === 0) {
      yesPercent = 20;
      noPercent = 20;
      naPercent = 20;
      tbdPercent = 20;
    } else {
      yesPercent = (yesCount / totalSelected) * 100;
      noPercent = (noCount / totalSelected) * 100;
      naPercent = (naCount / totalSelected) * 100;
      tbdPercent = (tbdCount / totalSelected) * 100;
    }

    setSelectedOptionCount({
      totalSelected: totalSelected,
      yes: {
        count: yesCount,
        percent: yesPercent,
      },
      no: {
        count: noCount,
        percent: noPercent,
      },
      na: {
        count: naCount,
        percent: naPercent,
      },
      tbd: {
        count: tbdCount,
        percent: tbdPercent,
      },
    });
  };


  const handleUpdateActionsPlanned = () => {
    let actionslist: any[] = [];
    let action_count: number = 0;

    sections.forEach((section: SectionData, sectionIndex: number) => {
      let actions: ActionData[] = [];
      let subSectionActions: {
        sub_section_id: string;
        title: string;
        actions: ActionData[];
      }[] = [];
      if (section.questions.length > 0) {
        section.questions.forEach((quest: any, questIndex: number) => {
          if (
            quest.actions_planned !== "" &&
            quest.actions_planned.length > 0
          ) {
            action_count++;
            actions.push({
              question_id: quest.question_id,
              question_index: questIndex + 1,
              question: quest.question,
              action: quest.actions_planned,
            });
          }
        });
      } else if (section.sub_sections.length > 0) {
        section.sub_sections.forEach((subsec: any, subsecIndex: number) => {
          let subsecQuestions: any[] = [];

          subsec.questions.forEach((quest: any, questIndex: number) => {
            if (
              quest.actions_planned !== "" &&
              quest.actions_planned.length > 0
            ) {
              action_count++;
              subsecQuestions.push({
                question_id: quest.question_id,
                question_index: questIndex + 1,
                question: quest.question,
                action: quest.actions_planned,
              });
            }
          });

          if (subsecQuestions.length > 0) {
            subSectionActions.push({
              sub_section_id: subsec.sub_section_id,
              title: subsec.title,
              actions: subsecQuestions,
            });
          }
        });
      }

      let data = {
        section_id: section._id,
        section: section.section,
        color: section.color,
        actions,
        sub_sections: subSectionActions,
      };
      if (actions.length > 0 || subSectionActions.length > 0) {
        actionslist.push(data);
      }
      setActionCount(action_count);
    });

    let actionData = {
      evaluation_id: evalScope._id.toString(),
      sections: actionslist,
    };
    setActionsPlanned(actionData);
  };

  const handleFormValidation = () => {
    let errorlist: { path: string; message: string }[] = [];
    sections.forEach((section: any) => {
      if (section.questions.length > 0) {
        section.questions.forEach((quest: any, questIndex: number) => {
          if (quest.response === "no" || quest.response === "tbd") {
            if (quest.justification === "" || quest.justification.length < 2) {
              errorlist.push({
                path: `sec-jus-${section._id}-${questIndex}`,
                message: "Justification should be atleast 2 characters",
              });
            }
            if (
              quest.actions_planned === "" ||
              quest.actions_planned.length < 2
            ) {
              errorlist.push({
                path: `sec-action-${section._id}-${questIndex}`,
                message: "Define proper actions planned",
              });
            }
          } else if (quest.response === "yes" || quest.response === "na") {
            if (quest.justification === "" || quest.justification.length < 2) {
              errorlist.push({
                path: `sec-jus-${section._id}-${questIndex}`,
                message: "Justification should be atleast 2 characters",
              });
            }
          }
        });
      } else if (section.sub_sections.length > 0) {
        section.sub_sections.forEach((subsec: any, subsecIndex: number) => {
          subsec.questions.forEach((quest: any, questIndex: number) => {
            if (quest.response === "no" || quest.response === "tbd") {
              if (
                quest.justification === "" ||
                quest.justification.length < 2
              ) {
                errorlist.push({
                  path: `subsec-jus-${section._id}-${subsecIndex}-${questIndex}`,
                  message: "Justification should be atleast 2 characters",
                });
              }
              if (
                quest.actions_planned === "" ||
                quest.actions_planned.length < 2
              ) {
                errorlist.push({
                  path: `subsec-action-${section._id}-${subsecIndex}-${questIndex}`,
                  message: "Define proper actions planned",
                });
              }
            } else if (quest.response === "yes" || quest.response === "na") {
              if (
                quest.justification === "" ||
                quest.justification.length < 2
              ) {
                errorlist.push({
                  path: `subsec-jus-${section._id}-${subsecIndex}-${questIndex}`,
                  message: "Justification should be atleast 2 characters",
                });
              }
            }
          });
        });
      }
    });
    if (errorlist.length > 0) {
      setErrorParams(errorlist);
      return {
        success: false,
        errors: errorlist
      }
    } else {
      return {
        success: true,
        errors:[]
      };
    }
  };

  const handleSaveEvaluationResponse = async () => {
    try {

      // validate the form
      let validation = handleFormValidation()
      if(validation.success === false){
        return {
          success: false,
          errors: validation.errors
        }
      }

      const formdata = {
        evaluation_id: evalScope._id,
        sections,
      };
      const res = await axios.put(
        `${API_URL}/evaluation/update/response`,
        formdata
      );
      if (res.status === 200 && res.data.success === true) {
        return {
          success: true,
        };
      } else {
        return {
          success: false,
        };
      }
    } catch (error) {
      console.log("Error saving evaluation responses:", error);
    }
  };

  const removeCollaborator = (cid:string) =>{
    setEvalScope({
      ...evalScope,
      collaborators: evalScope.collaborators.filter((col:any) =>{
        return col.cid.trim() !== cid.trim() 
      })
    })
  }
 

  useEffect(() => {
    handleOptionCountToPercent();
    handleUpdateActionsPlanned();
    // eslint-disable-next-line
  }, [sections]);

  const contextData = {
    evalScope,
    fetchEvaluationInfo,
    sections,
    setSections,
    questionLoading,
    questionCount,
    handleResponseSelect,
    sectionList,
    selectedSection,
    scrollToSection,
    fetchSectionsList,
    selectedOptionCount,
    handleSaveEvaluationResponse,
    handleResponseTextInputChange,
    actionsPlanned,
    actionCount,
    errorParams,
    removeCollaborator
  };

  return (
    <QuestionnaireContext.Provider value={contextData}>
      {children}
    </QuestionnaireContext.Provider>
  );
};
